import React, { ReactNode, memo, useLayoutEffect, useMemo, useRef } from "react";
import { Line } from "react-chartjs-2";

import {
  CategoryScale,
  Chart,
  ChartData,
  ChartOptions,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  ScriptableLineSegmentContext,
  TimeScale,
  Tooltip,
} from "chart.js";
import "chartjs-adapter-moment";
import ChartAnnotation, { AnnotationOptions } from "chartjs-plugin-annotation";

import { FieldSatelliteObservations, SatellitePass } from "services";

import { formatDayDate, useHorizontalWheelScroll } from "utils";

import { CHART_COLORS } from "consts";

import { CropFCPO } from "models";

import { DAY_MS, getMonthSpans, toDay, toTime } from "pages/admin/verifications/utils/satellite";

// Same setup as the admin NDVI page (NdviScores): time axis through the moment adapter, crop boxes through annotations.
Chart.register(CategoryScale, LinearScale, TimeScale, PointElement, LineElement, Legend, Tooltip, ChartAnnotation);

const WINDOW_FILL = "rgba(6, 56, 43, 0.06)";
const CROP_FILL = "rgba(16, 12, 6, 0.08)";
const TICK = { font: { family: "dazzed", weight: 500, size: 12 }, color: CHART_COLORS.black } as const;
const LINE = { borderWidth: 2, tension: 0.4, spanGaps: true } as const;

// Radar VH has its own axis in dB on the right. The usual range over farmland; the axis grows if a value falls outside.
const RADAR_DB_RANGE = { suggestedMin: -25, suggestedMax: -5 };
const axisTitle = (text: string) => ({ display: true, text, font: TICK.font, color: TICK.color });

// The NDVI line across fully cloudy passes, where there is no picture.
const CLOUD_GAP_DASH = [2, 4];

type ChartPoint = { x: string; y: number | null; pass: SatellitePass };

// A click picks the closest day with a picture, if one is this close.
const CLICK_REACH_MS = 3 * DAY_MS;

// Every month gets this much room, so the chart is wider than the drawer and scrolls sideways.
const MONTH_WIDTH_PX = 140;
const PX_PER_DAY = MONTH_WIDTH_PX / 30.4;
// Both y axes get a fixed width, so the plot (and the month slots under it) start at a known place.
const AXIS_WIDTH_PX = 56;
const fixAxisWidth = (scale: { width: number }) => {
  // eslint-disable-next-line no-param-reassign
  scale.width = AXIS_WIDTH_PX;
};

// The month labels sit this far below the plot, and the pinned axes stop above them, so a pinned axis never carries a
// month label over the months scrolled beneath it.
const MONTH_LABEL_GAP_PX = 10;

// Copies a strip of the chart canvas (an axis) onto a canvas that stays in place while the chart scrolls.
const copyStrip = (chart: Chart, target: HTMLCanvasElement | null, from: number, to: number) => {
  const context = target?.getContext("2d");
  if (!target || !context) return;
  const ratio = chart.currentDevicePixelRatio;
  const stripHeight = chart.chartArea.bottom + MONTH_LABEL_GAP_PX - 2;
  // Rounded like the canvas stores them, so an unchanged size is recognised at fractional screen scaling too.
  const [width, height] = [Math.round((to - from) * ratio), Math.round(stripHeight * ratio)];
  // Setting a canvas size clears and reallocates it, so only when the size changed.
  if (target.width !== width || target.height !== height) {
    Object.assign(target, { width, height });
    Object.assign(target.style, { width: `${to - from}px`, height: `${stripHeight}px` });
  } else {
    context.clearRect(0, 0, width, height);
  }
  context.drawImage(chart.canvas, from * ratio, 0, width, height, 0, 0, width, height);
};

type SatelliteChartProps = {
  observations: FieldSatelliteObservations;
  crops: CropFCPO[];
  coverCropWindowStart?: string | null;
  markerDate?: string | null;
  height?: number;
  /** Called with the day of a clicked dot that has a picture. */
  onPassClick?: (date: string) => void;
  /** Content shown under each month, as wide as that month on the chart and scrolled with it. */
  renderMonth?: (month: string) => ReactNode;
};

const getCropBoxes = (crops: CropFCPO[], from: string, to: string): Record<string, AnnotationOptions> =>
  Object.fromEntries(
    crops.flatMap((crop, index) => {
      const sown = crop.seedingStartDate ?? crop.fallbackDates.seedingStartDate;
      const harvested = crop.harvestEndDate ?? crop.harvestStartDate ?? crop.fallbackDates.harvestEndDate;
      if (!sown || !harvested || harvested < from || sown > to) return [];
      // Overlapping crops (a cover crop sown before the main crop's harvest) sit on two rows.
      const row = index % 2;
      const box: AnnotationOptions = {
        type: "box",
        xMin: sown < from ? from : sown,
        xMax: harvested > to ? to : harvested,
        yMin: 0.02 + row * 0.1,
        yMax: 0.1 + row * 0.1,
        backgroundColor: CROP_FILL,
        borderWidth: 0,
        label: { display: true, content: crop.cropName, font: { size: 11 }, color: CHART_COLORS.black },
      };
      return [[`crop-${crop.id}`, box]];
    })
  );

/**
 * NDVI (solid, dotted across cloudy passes without a picture) on the left axis and radar in dB (dashed) on the right,
 * the platform crops as boxes and the cover crop window shaded. Scrolls sideways with both axes pinned.
 */
export const SatelliteChart = memo(({ observations, crops, coverCropWindowStart, markerDate, height = 260, onPassClick, renderMonth }: SatelliteChartProps) => {
  const { range, passes } = observations;
  const scrollRef = useRef<HTMLDivElement>(null);
  const leftAxisRef = useRef<HTMLCanvasElement>(null);
  const rightAxisRef = useRef<HTMLCanvasElement>(null);

  const months = useMemo(() => getMonthSpans(range), [range]);
  const plotWidth = Math.round(months.reduce((sum, { days }) => sum + days, 0) * PX_PER_DAY);
  // The axis runs to the end of the last day, so each month spans exactly its days.
  const axisEnd = toDay(toTime(range.to) + DAY_MS);

  // Opens on the marked day (centred) or on the cover crop window. Read through a ref, so adding a year doesn't jump back.
  const rangeFrom = useRef(range.from);
  const focus = markerDate ?? coverCropWindowStart;
  useLayoutEffect(() => {
    const scroller = scrollRef.current;
    if (!scroller || !focus) return;
    const offset = ((toTime(focus) - toTime(rangeFrom.current)) / DAY_MS) * PX_PER_DAY;
    scroller.scrollLeft = markerDate ? offset + AXIS_WIDTH_PX - scroller.clientWidth / 2 : offset - MONTH_WIDTH_PX / 2;
  }, [focus, markerDate]);

  useHorizontalWheelScroll(scrollRef);

  // An earlier year grows the chart on the left. Shift by its width, so the view stays on the same days.
  useLayoutEffect(() => {
    const scroller = scrollRef.current;
    const addedDays = (toTime(rangeFrom.current) - toTime(range.from)) / DAY_MS;
    if (scroller && addedDays > 0) scroller.scrollLeft += addedDays * PX_PER_DAY;
    rangeFrom.current = range.from;
  }, [range.from]);

  // The axes only move when the chart is laid out again, so they're copied after a layout, not on every hover redraw.
  const pinAxes = useMemo(() => {
    let isLaidOut = false;
    return {
      id: "pinAxes",
      afterLayout: () => {
        isLaidOut = true;
      },
      afterRender: (chart: Chart) => {
        if (!isLaidOut) return;
        isLaidOut = false;
        copyStrip(chart, leftAxisRef.current, 0, chart.chartArea.left);
        copyStrip(chart, rightAxisRef.current, chart.chartArea.right, chart.width);
      },
    };
  }, []);

  // Each line has only its own satellite's days. NDVI keeps fully cloudy Sentinel-2 passes as gaps (y null), so the line
  // across them can be drawn dotted: no picture there.
  const data = useMemo<ChartData<"line", ChartPoint[]>>(
    () => ({
      datasets: [
        {
          ...LINE,
          label: "NDVI",
          data: passes.filter(({ fieldCloudCoverPct }) => fieldCloudCoverPct !== null).map((pass) => ({ x: pass.date, y: pass.ndvi, pass })),
          borderColor: CHART_COLORS["green-500"],
          backgroundColor: CHART_COLORS["green-500"],
          pointRadius: 2.5,
          pointHoverRadius: 5,
          segment: { borderDash: (segment: ScriptableLineSegmentContext) => (segment.p0.skip || segment.p1.skip ? CLOUD_GAP_DASH : undefined) },
        },
        {
          ...LINE,
          label: "Radar",
          yAxisID: "radar",
          data: passes.filter(({ radarVhDb }) => radarVhDb !== null).map((pass) => ({ x: pass.date, y: pass.radarVhDb, pass })),
          borderColor: CHART_COLORS["blue-500"],
          backgroundColor: CHART_COLORS["blue-500"],
          borderDash: [4, 4],
          pointRadius: 0,
        },
      ],
    }),
    [passes]
  );

  // The days a click can open: the same hasImage days the day modal steps through.
  const pictureDays = useMemo(() => passes.filter(({ hasImage }) => hasImage).map(({ date }) => ({ date, time: toTime(date) })), [passes]);

  const options = useMemo<ChartOptions<"line">>(() => {
    const nearestPictureDay = (chart: Chart, x: number | null) => {
      const time = x === null ? undefined : chart.scales.x?.getValueForPixel(x);
      if (time === undefined) return undefined;
      const nearest = pictureDays.reduce<{ date: string; gap: number } | undefined>((best, day) => {
        const gap = Math.abs(day.time - time);
        return !best || gap < best.gap ? { date: day.date, gap } : best;
      }, undefined);
      return nearest && nearest.gap <= CLICK_REACH_MS ? nearest.date : undefined;
    };

    const annotations = getCropBoxes(crops, range.from, range.to);
    if (coverCropWindowStart) {
      annotations.window = {
        type: "box",
        xMin: coverCropWindowStart < range.from ? range.from : coverCropWindowStart,
        xMax: range.to,
        backgroundColor: WINDOW_FILL,
        borderWidth: 0,
        label: { display: true, content: "Cover crop window", position: { x: "center", y: "start" }, font: { size: 11 }, color: CHART_COLORS["green-500"] },
      };
    }
    if (markerDate) {
      annotations.marker = { type: "line", xMin: markerDate, xMax: markerDate, borderColor: CHART_COLORS.black, borderWidth: 2 };
    }

    return {
      responsive: true,
      maintainAspectRatio: false,
      // A chart scrolled back many years is very wide. Capped, so it stays inside the browser's canvas size limit.
      devicePixelRatio: Math.min(window.devicePixelRatio, 2),
      interaction: { mode: "nearest", axis: "x", intersect: false },
      onClick: (event, _elements, chart) => {
        const date = nearestPictureDay(chart, event.x);
        if (date && onPassClick) onPassClick(date);
      },
      onHover: (event, _elements, chart) => {
        const target = event.native?.target as HTMLElement | undefined;
        if (target) target.style.cursor = onPassClick && nearestPictureDay(chart, event.x) ? "pointer" : "default";
      },
      scales: {
        x: {
          type: "time",
          time: { unit: "month", displayFormats: { month: "MMM YYYY" } },
          min: range.from,
          max: axisEnd,
          grid: { display: false },
          ticks: { ...TICK, align: "start", padding: MONTH_LABEL_GAP_PX },
        },
        y: { position: "left", min: 0, max: 1, title: axisTitle("NDVI"), ticks: { ...TICK, stepSize: 0.25 }, afterFit: fixAxisWidth },
        // Its own scale: where the two lines cross or run together means nothing.
        radar: {
          position: "right",
          ...RADAR_DB_RANGE,
          title: axisTitle("Radar VH (dB)"),
          grid: { drawOnChartArea: false },
          ticks: TICK,
          afterFit: fixAxisWidth,
        },
      },
      plugins: {
        // datalabels is registered globally elsewhere in the app.
        datalabels: { display: false },
        // Drawn outside the scrolling canvas, so it stays in view.
        legend: { display: false },
        annotation: { annotations },
        tooltip: {
          callbacks: {
            // The item's label is the time axis' own formatting, so the day comes from the pass itself.
            title: (items) => {
              const pass = (items[0]?.raw as ChartPoint | undefined)?.pass;
              return pass ? formatDayDate(pass.date) : "";
            },
            label: ({ datasetIndex, raw }) => {
              const { pass } = raw as ChartPoint;
              if (datasetIndex === 0) return pass.ndvi === null ? "NDVI: cloudy" : `NDVI: ${pass.ndvi.toFixed(2)}`;
              return pass.radarVhDb === null ? "" : `Radar VH: ${pass.radarVhDb.toFixed(1)} dB`;
            },
          },
        },
      },
    };
  }, [pictureDays, crops, range, axisEnd, coverCropWindowStart, markerDate, onPassClick]);

  return (
    <div className="satellite-chart">
      <div className="satellite-chart-legend">
        <span className="satellite-legend-ndvi">NDVI</span>
        <span className="satellite-legend-cloud">NDVI across cloud, no picture</span>
        <span className="satellite-legend-radar">Radar</span>
      </div>
      <div className="satellite-chart-frame">
        <div ref={scrollRef} className="satellite-chart-scroll">
          <div style={{ width: plotWidth + 2 * AXIS_WIDTH_PX }}>
            <div style={{ height }}>
              <Line options={options} data={data} plugins={[pinAxes]} />
            </div>
            {renderMonth && (
              <div className="satellite-chart-months" style={{ padding: `0 ${AXIS_WIDTH_PX}px` }}>
                {months.map(({ month, days }) => (
                  <div key={month} style={{ width: days * PX_PER_DAY }}>
                    {renderMonth(month)}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <canvas ref={leftAxisRef} className="satellite-chart-axis satellite-chart-axis-left" />
        <canvas ref={rightAxisRef} className="satellite-chart-axis satellite-chart-axis-right" />
      </div>
    </div>
  );
});

SatelliteChart.displayName = "SatelliteChart";
